<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import VChart from 'vue-echarts'
import { PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { useQuery } from '@tanstack/vue-query'
import { yunFormat } from '@mp/utils'
import { todayOverview, todayPayType } from '@mp/api/mchTradeData/BpIndexDashboardController.js'
import { getPayTypeColorWithIcon } from '@/utils'

use([CanvasRenderer, PieChart, TooltipComponent, LegendComponent, GridComponent, TitleComponent])

type TradingType = 'paidAmount' | 'payCount' | 'refundAmount' | 'refundCount'
const tabActive = ref<TradingType>('paidAmount')
const isAmount = computed(() => (tabActive.value === 'paidAmount' || tabActive.value === 'refundAmount'))

const option = ref({
  grid: {
    bottom: 0,
    left: '20%',
    right: '20%',
  },
  title: {
    text: '...',
    subtext: '00.00',
    x: 'center',
    y: '40%',
    textStyle: {
      fontSize: 15,
      fontWeight: 'normal',
      color: '#606266',
    },
    subtextStyle: {
      color: '#1B2A3E',
      fontSize: 24,
      fontWeight: 'bold',
    },
  },
  tooltip: {
    trigger: 'item',
  },
  series: [
    {
      legendHoverLink: false,
      name: '商户占比',
      type: 'pie',
      radius: ['50%', '80%'],
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2,
      },
      label: {
        show: true,
        position: 'outer',
        formatter: (e: any) => `{z|${e.name}${e.percent}%} \n {x|${e.value}}`,
        rich: {
          z: {
            color: '#606266',
          },
          x: {
            fontSize: 14,
            fontWeight: 'bold',
            color: '#303133',
            padding: [5, 0, 0, 0],
          },
        },
      },
      data: [] as unknown[],
    },
  ],
})

const { data } = useQuery({
  queryKey: ['specifyDatePayType'],
  queryFn: () => todayPayType({}),
  select: data => data.rows || [],
})

// #查询总收款
const { data: overviewData } = useQuery({
  queryKey: ['statement/day/stat/trade/specifyDate/overview'],
  queryFn: () => todayOverview({}),
  select: data => data?.data,
})

watchEffect(() => {
  option.value.series[0].data = data.value?.map(item => ({
    name: item.payTypeDesc,
    value: isAmount.value ? yunFormat(item[tabActive.value]) : item[tabActive.value],
    itemStyle: {
      color: getPayTypeColorWithIcon(item.payType as string).color,
    },
    ...item,
  })) as unknown[]

  // ?设置总
  if (!overviewData.value)
    return
  option.value.title.text = `${getLabel(tabActive.value)}`
  option.value.title.subtext = isAmount.value ? `${yunFormat(overviewData?.value[tabActive.value])}` : `${overviewData?.value[tabActive.value]}`
})

function getLabel(type: TradingType) {
  if (type === 'paidAmount')
    return '总收款(元)'
  if (type === 'refundAmount')
    return '总退款(元)'
  if (type === 'payCount')
    return '总收款笔数'
  if (type === 'refundCount')
    return '总退款笔数'

  return '未定义'
}
</script>

<template>
  <section class="bg-white p-4 rounded-lg relative overflow-hidden" style="--el-border-color-light: transparent">
    <div class="flex justify-between">
      <label class="text-base text-n-800 flex font-medium">今日渠道交易数据

      </label>

      <el-tabs v-model="tabActive" class=" -mt-[10px]" stretch style="--el-text-color-primary: var(--n-400)">
        <el-tab-pane name="paidAmount">
          <template #label>
            <span id="PAY_TYPE_PAID_AMOUNT_LABEL" v-track>收款金额</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="refundAmount">
          <template #label>
            <span id="PAY_TYPE_REFUND_AMOUNT_LABEL" v-track>退款金额</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="payCount">
          <template #label>
            <span id="PAY_TYPE_PAY_COUNT_LABEL" v-track>收款笔数</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="refundCount">
          <template #label>
            <span id="PAY_TYPE_REFUND_COUNT_LABEL" v-track>退款笔数</span>
          </template>
        </el-tab-pane>
      </el-tabs>
    </div>

    <p class="text-n-90 text-xs -mt-3">
      更新时间 {{ overviewData?.statTime }}
    </p>

    <VChart autoresize class="h-[300px] mt-6" :option="option" />
  </section>
</template>

<style scoped>
:deep(.el-tabs__item) {
  padding: 0 10px;
}
</style>
